import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

// SECURITY NOTE: JWT is stored in localStorage by design (user-approved trade-off).
// - Pros: simple cross-tab access, works with current CORS + Authorization header flow.
// - Cons: readable by JS → susceptible to XSS exfiltration. Mitigations in place:
//     (1) Strict CORS allow-list + allow_credentials=True on the backend.
//     (2) No third-party scripts mounted at runtime (Stripe Elements is the only one and is sandboxed in an iframe).
//     (3) React auto-escapes interpolated content; no dangerouslySetInnerHTML in the app today.
// Future migration path: switch to httpOnly Secure SameSite=Strict cookies + CSRF token on mutations.
// Tracked in ROADMAP under "Auth hardening".

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('gulfbridge_token');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get('/auth/me');
            setUser(data.user);
        } catch {
            localStorage.removeItem('gulfbridge_token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        localStorage.setItem('gulfbridge_token', data.token);
        setUser(data.user);
        return data.user;
    };

    const register = async (email, password, name, role) => {
        const { data } = await api.post('/auth/register', { email, password, name, role });
        localStorage.setItem('gulfbridge_token', data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem('gulfbridge_token');
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data.user);
        } catch (err) {
            // Non-fatal: surface for debugging but don't disrupt UI flow
            console.debug('AuthContext.refreshUser failed:', err?.message || err);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}
